/* eslint-disable  @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { Divider, Grid, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Logger } from '../../../../../libs/utils/logger';
import FxCard from '../../../../Container/FxCard';
import FxCardHeader from '../../../../Container/FxCardHeader';
import FxCardBody from '../../../../Container/FxCardBody';
import FxCardFooter from '../../../../Container/FxCardFooter';
import { FxButton } from '../../../../Action/FxButton';
import { IFileInfo } from '../../../../Data/FxAgreementLinkCard';
import { FxAgreementLinkCard } from '../../../../Data/FxAgreementLinkCard';
import FxCheckEdit from '../../../../Input/FxCheck/FxCheckEdit';
import { AUTH_STRINGS } from '../../../../../constants/strings';
import { ReactComponent as MultipleUser } from '../../../../../assets/svg/multiple-user-icon.svg';
import { useMediaQuery } from 'react-responsive';
import { JointTenancyCoOwnerReviewInfoCard } from './JointTenancyCoOwnerReviewInfoCard';
import { setLocalStorage } from '../../../../../libs/utils/cacheHandler';
import { useHistory } from 'react-router-dom';
import { callAPI, getBase64FromPDF, Method } from '../../../../Utils/CommonBaseClass';
import { getCustomerUrl, processAPIResponse } from '../../../../../libs/utils/utils';
import { getKey, setKey } from '../../../../../libs/utils/storageManager';
import FxSnackBar from '../../../../Utils/fx-snack-bar';

/**
 * Author: Shivaraj PP
 * Component: JointTenancyCoOwnerReviewAndSubmitSignup
 *  Description: JointTenancyCoOwnerReviewAndSubmitSignup component
 */


Logger.debug("JointTenancyCoOwnerReviewAndSubmitSignup.tsx", "Review and Submit Signup initializing")

interface InputErrType {
    type: string;
    message: string;
}


interface IReviewAndSubmitSignup {
    onSubmit: (data: any,req:any,index:number) => void;
    loading: boolean;
    reviewDetails:any;
}

export const JointTenancyCoOwnerReviewAndSubmitSignup: React.FC<IReviewAndSubmitSignup> = React.memo(
    (props) => {
        const { register, formState: { errors }, setValue, control } = useForm();
        const [btnDisabled, setBtnDisabled] = useState(true);
        const [fileInfo, setFileInfo] = useState<IFileInfo[]>([]);
        const isSmallScreen = useMediaQuery({ query: '(max-width: 600px)' });
        const history = useHistory();
        const customerIndividualFullName = getKey('CustomerIndividualFullName')
        const customerName = getKey('CustomerName')
        /**
         * Handles form submission.
         * 
         * - Attaches `fileInfo` to the `data` object if available.
         * - Calls the `props.onSubmit` callback with the submitted data, request object, and index.
         *
         * @param data - The form data being submitted.
         * @param req - The request-related object (e.g., for API or tracking).
         * @param index - The index indicating the current form or item in context.
         */
        async function onSubmit(data: any,req:any,index:number) {
            if (fileInfo) {
                data['fileData'] = fileInfo
            }
            if (props.onSubmit) {
                props.onSubmit(data,req,index);
            }
        }

        /**
 * Handles the click event to accept a customer agreement.
 *
 * - Retrieves the initial customer account ID and authenticated user ID.
 * - Builds the payload with the user ID to send with the request.
 * - Constructs the API URL using the customer account ID.
 * - Sends a POST request to accept the agreement.
 * - Processes the API response:
 *    - If successful, clears the 'signUpFlow' from local storage and redirects to the home page.
 *    - If failed, displays an error message using FxSnackBar.
 */
        async function handleAcceptAgreement (){
            try {
                const customerInitialAccount = getKey('customerInitialAccount');
                let authUserId = getKey('authUserId')
                const payload = {
                    "owners": [
                        {
                            "id": authUserId
                        }
                    ]
                }
                let currentUser = props?.reviewDetails?.find((item:any) => item?.id === authUserId)

                if(currentUser.fullName !== customerIndividualFullName || currentUser.fullName !== customerName){
                    setKey("CustomerIndividualFullName", currentUser.fullName);
                    setKey("CustomerName", currentUser.fullName);
                }
                const url:any=getCustomerUrl(`account/id/${customerInitialAccount?.id}/acceptAgreement`);
                let status = await callAPI(url, Method.POST, payload)
                status = processAPIResponse(status);
                if(status.status){
                    setLocalStorage('signUpFlow',undefined);
                    history.push('/home');
                }else {
                    FxSnackBar.show({
                        text: status.message,
                    });
                }
            } catch (error) {           
                Logger.error("JointTenancyCoOwnerReviewAndSubmitSignup.tsx :: handleAcceptAgreement", "error", error);
            }
            
        }

        /**
         * Returns a user-friendly error message based on the input error type.
         *
         * - If the error type is 'required', returns a predefined message from AUTH_STRINGS.
         * - Otherwise, returns the original error message.
         *
         * @param err - The input error object containing type and message.
         * @returns A string representing the error message to display.
         */
        const renderError = (err: InputErrType): string => {
            if (err.type === 'required') {
                return AUTH_STRINGS.ERRORS.AGREE_PLEASE_ENTER;
            }
            return err.message;
        };
        // Privacy policy options
        const privacyPolicyData = [
            { label: 'By submitting this form, you agree to the Passport Account agreement, to receiving electronic communication from Passport, and you certify that the information provided is complete and correct.', value: 'yes' }
        ];
       /**
         * Handles the checkbox change event to enable or disable a button.
         *
         * - If the checkbox is checked, the button is enabled.
         * - If unchecked, the button is disabled.
         *
         * @param event - The change event from the checkbox input.
         */
        const handleChange = (event: React.ChangeEvent<HTMLInputElement> | any) => {
            if (event.target.checked === true) {
                setBtnDisabled(false)
            }
            else {
                setBtnDisabled(true)
            }
        }
        return (
            <Grid xs={12} className='signup-detail-page-bg'>
                <Grid container id="create-external-account-main-grid" xs={12} justifyContent="center">
                    <Grid item xs={12} sm={isSmallScreen ? undefined : 11} md={isSmallScreen ? undefined : 7}>
                        <FxCard id="create-external-account-form-card" className="fx-theme-passport-no-before fx-no-shadow fx-signup-formkyc fx-business-details fx-signup-formkyc-individual-margin">
                                <FxCardHeader className='signup-review-page-header-container fx-margin-bottom-2' id="create-external-account-form-card-header">
                                    <Grid item xs={12} className={'fx-signup-formkyc-topblock'}>
                                        <Grid item xs={12} container flexWrap={'nowrap'} alignItems={'center'}>
                                            <Grid item className="fx-signup-formkyc-topblock-icon">
                                                <MultipleUser />
                                            </Grid>
                                            <Grid item>
                                                <Typography className='signup-review-page-header' >
                                                    Almost done
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </FxCardHeader>
                                <FxCardBody id="create-external-account-form-card-body" className="fx-info-card fx-margin-top-reducer">
                                    {/* joint tendancy co-owner review info card component */}
                                    <JointTenancyCoOwnerReviewInfoCard owners={props?.reviewDetails} onSubmit={onSubmit}/>

                                    <Grid container className="fx-info-card-form fx-kyc-agree" >
                                        <Grid item xs={12}>
                                            <FxAgreementLinkCard
                                                agreementTypes={['PassportAccount', 'RateCard']}
                                                onFileInfoReady={setFileInfo}
                                            />
                                        </Grid>
                                        <Grid item container xs={12} className="flex column fx-kyc-agree-label fx-review-business-signup-agree">
                                            {!!fileInfo?.length && <Grid item xs={12} className="fx-submit-agree">
                                                <FxCheckEdit register={{ ...register("privacyPolicyAccepted") }} rules={{ required: "Please click Agree" }} control={control} onChange={(e: any) => { handleChange(e); }} id="privacy-policy-accepted" name="privacyPolicyAccepted" data={privacyPolicyData} className="fx-privacy-text" row="vertical" setValue={setValue} />
                                            </Grid>}
                                            <Grid item xs={12} className={'error-message'}>
                                                {errors.privacyPolicyAccepted && renderError(errors.privacyPolicyAccepted)}
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={12} marginBottom={2}>
                                            <Divider />
                                        </Grid>
                                        {isSmallScreen && <><Grid item xs={12}></Grid><Grid item xs={12}></Grid></>}
                                    </Grid>
                                </FxCardBody>
                                <FxCardFooter id="create-external-account-form-card-footer" className="fx-footer">
                                    <Grid container direction="row" justifyContent="flex-end" className={isSmallScreen ? "fx-modal-footer fx-footer-kyc" : "fx-modal-footer"}>
                                        <FxButton variant="contained"
                                            className="fx-button fx-button-cancel"
                                            id="ea-modal">
                                            Go Back
                                        </FxButton>
                                        <span className="fx-padding-right-16" />
                                        <FxButton
                                            disableRipple={false}
                                            className={btnDisabled ? "fx-button fx-button-theme-disabled" : "fx-button fx-button-theme"}
                                            id={"create-external-account-form-card-save-button"}
                                            type="submit"
                                            disabled={btnDisabled}
                                            isSubmitting={props?.loading}
                                            onClick={handleAcceptAgreement}
                                        >
                                            Submit
                                        </FxButton>
                                    </Grid>
                                </FxCardFooter>
                        </FxCard>
                    </Grid>
                </Grid>
            </Grid>
        )
    })
